import {
  Box,
  ChakraProvider,
  extendTheme,
  Heading,
  Icon,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import ReactDOM from "react-dom/client";
import App from "./app";

import "@fontsource/barlow/latin-400.css";
import "@fontsource/barlow/latin-500.css";
import "@fontsource/barlow/latin-600.css";
import "@fontsource/barlow/latin-700.css";
import "@fontsource/barlow-condensed/latin-600.css";
import "@fontsource/barlow-condensed/latin-700.css";
import "@fontsource/barlow-semi-condensed/latin-500.css";
import "@fontsource/barlow-semi-condensed/latin-600.css";
import "@fontsource/barlow-semi-condensed/latin-700.css";
import "./instrument";
import "./localization/i18n";

import { AuthProvider } from "./auth/auth-context/auth-context";
import { ApolloProvider } from "@apollo/client";
import { ErrorBoundary as SentryErrorBoundary } from "@sentry/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { IdleTimerProvider } from "react-idle-timer";
import { BrowserRouter } from "react-router-dom";
import { apolloClient } from "./apollo";
import { Button } from "./components/chackra-ui-button";
import { KioskProvider } from "./kiosk/kiosk-context";

const colors = {
  blue: {
    "50": "#B8D9FF",
    "100": "#A3CEFF",
    "200": "#7AB9FF",
    "300": "#52A3FF",
    "400": "#298EFF",
    "500": "#0078FF",
    "600": "#005EC7",
    "700": "#00438F",
    "800": "#002957",
    "900": "#000E1F",
  },
  slate: {
    "50": "#F8FAFC",
    "100": "#F1F5F9",
    "200": "#E2E8F0",
    "300": "#CBD5E1",
    "400": "#94A3B8",
    "500": "#64748B",
    "600": "#475569",
    "700": "#334155",
    "800": "#1E293B",
    "900": "#0F172A",
  },
  senseiDark: "#13192E",
};

const fonts = {
  heading: `'Barlow Semi Condensed', sans-serif`,
  body: `'Barlow', sans-serif`,
};

const inputBorderRadius = "16px";

const theme = extendTheme({
  colors,
  fonts,
  styles: {
    global: {
      body: {
        bg: "white",
        color: "#13192E",
      },
    },
  },
  components: {
    Button: {
      baseStyle: { borderRadius: "12px" },
      variants: {
        senseiFilled: {
          bg: "#13192E",
          color: "white",
          minH: "48px",
          px: 6,
          fontWeight: 700,
          _hover: { opacity: 0.9 },
          _active: { opacity: 0.85 },
        },
        senseiOutlined: {
          bg: "slate.50",
          color: "#13192E",
          border: "none",
          minH: "48px",
          px: 6,
          fontWeight: 700,
          _hover: { bg: "slate.100" },
        },
      },
    },
    Input: {
      variants: {
        outline: {
          field: {
            bg: "white",
            borderWidth: "1.5px",
            borderColor: "slate.300",
            _hover: { borderColor: "slate.400" },
            _focus: {
              borderColor: "#13192E",
              boxShadow: "0 0 0 3px rgba(19, 25, 46, 0.06)",
            },
            _disabled: {
              bg: "slate.50",
              color: "slate.400",
              cursor: "not-allowed",
              opacity: 1,
            },
          },
        },
      },
      sizes: {
        xs: { field: { borderRadius: inputBorderRadius } },
        sm: { field: { borderRadius: inputBorderRadius } },
        md: { field: { borderRadius: inputBorderRadius, h: "48px", fontSize: "16px" } },
        lg: { field: { borderRadius: inputBorderRadius, h: "48px", fontSize: "16px" } },
      },
    },
    Select: {
      variants: {
        outline: {
          field: {
            bg: "white",
            borderWidth: "1.5px",
            borderColor: "slate.300",
            _hover: { borderColor: "slate.400" },
            _disabled: {
              bg: "slate.50",
              color: "slate.400",
              cursor: "not-allowed",
              opacity: 1,
            },
          },
        },
      },
      sizes: {
        xs: { field: { borderRadius: inputBorderRadius } },
        sm: { field: { borderRadius: inputBorderRadius } },
        md: { field: { borderRadius: inputBorderRadius, h: "52px", fontSize: "16px" } },
        lg: { field: { borderRadius: inputBorderRadius, h: "52px", fontSize: "16px" } },
      },
    },
    Tag: {
      baseStyle: {
        container: { borderRadius: "full" },
      },
    },
  },
});

const FallbackComponent = () => {
  const { t } = useTranslation();
  return (
    <Box
      height="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="gray.50"
    >
      <VStack spacing={4} textAlign="center" maxWidth="640px" p={4}>
        <Icon as={ExclamationCircleIcon} w={12} h={12} color="red.500" />
        <Heading size="lg">{t("errorBoundary.title")}</Heading>
        <Text>{t("errorBoundary.message")}</Text>
        <Button
          colorScheme="blue"
          onClick={() => window.location.reload()}
        >
          {t("errorBoundary.refreshButton")}
        </Button>
      </VStack>
    </Box>
  );
};

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ChakraProvider theme={theme}>
      <SentryErrorBoundary fallback={FallbackComponent}>
        <KioskProvider>
          <AuthProvider>
            <ApolloProvider client={apolloClient}>
              <BrowserRouter>
                <IdleTimerProvider>
                  <App />
                </IdleTimerProvider>
              </BrowserRouter>
            </ApolloProvider>
          </AuthProvider>
        </KioskProvider>
      </SentryErrorBoundary>
    </ChakraProvider>
  </React.StrictMode>
);
